/* eslint-disable no-case-declarations */
/* eslint-disable indent */
import { CollectionType, INotificationContext } from '@/types'
import { IEditionDynamicTrades } from '@/types/IEdition'
import { Notification, NotificationType } from 'api/notifications'
import { TagTheme } from 'components/notifications/NotificationTags'
import { Currencies, Currency } from 'constants/currencies'
import { TypeToContentMap } from 'constants/notifications'
import routes from 'constants/routes'
import {
  DOCUMENT_STATUS,
  NOTIFICATION_STATUS,
  SHIPMENT_STATUS,
} from 'constants/statuses'
import { useCurrencies } from 'contexts/hooks/useCurrencies'
import dayjs from 'dayjs'
import { Translate } from 'next-translate'
import { Region } from 'types'
import { FlatCollectionType } from 'utils/data'
import { formatLocalDate } from 'utils/dates'
import { pickDynamicPrice, pickDynamicTradesPrices } from 'utils/dynamic-prices'
import { getLink } from 'utils/strings'
import {
  getArtistUrl,
  getArtworkUrl,
  getArtworkUrlFromRoot,
  getBuyNowUrl,
  getEditAskUrl,
  getEditBidUrl,
  getOrderUrl,
  getSaleUrl,
  getSellNowUrl,
  getSubmitDocumentsUrl,
} from 'utils/urls'

export function getNotificationPrice(
  userCurrency: Currency,
  userRegion: Region | null,
  context?: INotificationContext
) {
  if (!context) {
    return null
  }

  const pickedPrice = pickDynamicPrice({
    prices: context,
    userCurrency,
    userRegion,
  })

  return pickedPrice
}

function convert<T extends CollectionType>(
  input: FlatCollectionType<T> | undefined | null
) {
  if (!input) return null
  return { data: { id: input.id, attributes: input } }
}
function mapTrades(notification: Notification): IEditionDynamicTrades | null {
  const mainEdition = notification?.artwork?.editions?.find(
    e => e.name === 'Main'
  )
  if (!mainEdition) return null

  return {
    highestBidApac: convert(mainEdition.highestBidApac),
    highestBidCanMex: convert(mainEdition.highestBidCanMex),
    highestBidEu1: convert(mainEdition.highestBidEu1),
    highestBidEu2: convert(mainEdition.highestBidEu2),
    highestBidMeast: convert(mainEdition.highestBidMeast),
    highestBidRow: convert(mainEdition.highestBidRow),
    highestBidUk: convert(mainEdition.highestBidUk),
    highestBidUs: convert(mainEdition.highestBidUs),
    lowestAskApac: convert(mainEdition.lowestAskApac),
    lowestAskCanMex: convert(mainEdition.lowestAskCanMex),
    lowestAskEu1: convert(mainEdition.lowestAskEu1),
    lowestAskEu2: convert(mainEdition.lowestAskEu2),
    lowestAskMeast: convert(mainEdition.lowestAskMeast),
    lowestAskRow: convert(mainEdition.lowestAskRow),
    lowestAskUk: convert(mainEdition.lowestAskUk),
    lowestAskUs: convert(mainEdition.lowestAskUs),
  }
}

export function determineNotificationPrices(
  userCurrency: Currency,
  userRegion: Region | null,
  notification: Notification
) {
  const { context, hasAsk, hasBid } = notification
  const price = getNotificationPrice(userCurrency, userRegion, context)

  const mappedTrades = mapTrades(notification)
  const prices = mappedTrades
    ? pickDynamicTradesPrices({
        trades: mappedTrades,
        userCurrency,
        userRegion,
      })
    : null

  const highestBidPrice = prices?.highestBid
  const lowestAskPrice = prices?.lowestAsk

  const ownBidPrice = hasBid
    ? pickDynamicPrice({ prices: hasBid, userCurrency, userRegion })
    : null

  const ownAskPrice = hasAsk
    ? pickDynamicPrice({ prices: hasAsk, userCurrency, userRegion })
    : null

  return {
    price,
    highestBidPrice,
    lowestAskPrice,
    lowestAskPriceGbp: prices?.lowestAskGbp,
    ownBidPrice,
    ownAskPrice,
  }
}

/**
 * The current status of a sale notification, depending on the context (auth status, auth deadline, etc.).
 */
export interface NotificationStatusInfo {
  /**
   * The determined status of the sale notification.
   */
  status?: string
  /**
   * The deadline date for an action.
   */
  deadlineDate?: dayjs.Dayjs
  /**
   * The number of days until the deadline for an action.
   */
  deadlineDays?: number
}

/**
 * Determines the status, deadline date and how many days until the deadline for a SALE notification, depending on multiple fields.
 */
export function determineSaleNotificationStatusInfo(
  notification: Notification
): NotificationStatusInfo {
  if (notification.type !== NotificationType.SALE || !notification.context)
    return {}

  const {
    documentDeadline,
    documentStatus,
    shippingDeadline,
    shippingStatus,
    generateLabelFailed,
  } = notification.context

  if (generateLabelFailed) {
    return { status: NOTIFICATION_STATUS.AWAITING_LABEL }
  }

  const currentDate = dayjs().set('hour', 0).set('minute', 0).set('second', 0)

  if (documentStatus === DOCUMENT_STATUS.submitted) {
    return { status: NOTIFICATION_STATUS.AUTH_REVIEW }
  }

  // documents are required, the user should upload them
  if (documentStatus === DOCUMENT_STATUS.required && documentDeadline) {
    const authDeadlineDate = dayjs(documentDeadline)
    const authDeadlineDays = Math.ceil(
      authDeadlineDate.diff(currentDate, 'days', true)
    )
    const authOverdue =
      typeof authDeadlineDays === 'number' && authDeadlineDays < 0

    if (!authOverdue) {
      return {
        status: NOTIFICATION_STATUS.AUTH_REQUIRED,
        deadlineDate: authDeadlineDate,
        deadlineDays: authDeadlineDays,
      }
    }

    if (authOverdue) {
      return {
        status: NOTIFICATION_STATUS.AUTH_OVERDUE,
        deadlineDate: authDeadlineDate,
        deadlineDays: authDeadlineDays,
      }
    }
  }

  // documents are not required, the user should send the artwork
  if (
    (documentStatus === DOCUMENT_STATUS.not_required ||
      documentStatus === DOCUMENT_STATUS.approved) &&
    shippingStatus === SHIPMENT_STATUS.READY_TO_SEND &&
    shippingDeadline
  ) {
    const shippingDeadlineDate = dayjs(shippingDeadline)
    const shippingDeadlineDays = Math.ceil(
      shippingDeadlineDate.diff(currentDate, 'day', true)
    )
    const shippingOverdue =
      typeof shippingDeadlineDays === 'number' && shippingDeadlineDays < 0

    if (!shippingOverdue) {
      return {
        status:
          documentStatus === DOCUMENT_STATUS.approved
            ? NOTIFICATION_STATUS.AUTH_APPROVED
            : NOTIFICATION_STATUS.SHOULD_SHIP,
        deadlineDate: shippingDeadlineDate,
        deadlineDays: shippingDeadlineDays,
      }
    }

    if (shippingOverdue) {
      return {
        status: NOTIFICATION_STATUS.SHIPPING_OVERDUE,
        deadlineDate: shippingDeadlineDate,
        deadlineDays: shippingDeadlineDays,
      }
    }
  }

  return {}
}

function determineDeadlineTag(
  deadlineDays: number | undefined,
  labels: { today: string; tomorrow: string; future: string }
) {
  if (typeof deadlineDays === 'undefined') return undefined
  if (deadlineDays <= 0) return labels.today
  if (deadlineDays === 1) return labels.tomorrow
  return labels.future
}

function determineShipTag(deadlineDays: number | undefined) {
  return determineDeadlineTag(deadlineDays, {
    today: 'ship_today',
    tomorrow: 'ship_by_tomorrow',
    future: 'ship_within_x_days',
  })
}

function determineDocsTag(deadlineDays: number | undefined) {
  return determineDeadlineTag(deadlineDays, {
    today: 'add_docs_today',
    tomorrow: 'add_docs_by_tomorrow',
    future: 'add_docs_within_x_days',
  })
}

/**
 * The additional information that appears when a notification is extended (when the user is on the notifications page and clicks one of the notifications).
 */
export interface NotificationInfo {
  /**
   * The title of the information.
   */
  title?: string
  /**
   * The title which should be displayed only on mobile devices.
   */
  titleMobile?: string
  /**
   * The description of the information.
   */
  description?: string
  /**
   * The description which should be displayed only on mobile devices.
   */
  descriptionMobile?: string
  /**
   * The translation payload that should be passed to both the title and description. Here some values might be injected so they don't need to be computed again when displaying the elements.
   */
  translationPayload?: Record<string, string | number | undefined>
  /**
   * If provided, displays a button with the translated text.
   *
   * Should be a translation key.
   */
  buttonText?: string
  /**
   * Determines where to redirect the user when clicking the button,
   */
  buttonAction?: string
}

export function determineSaleNotificationContent(notification: Notification): {
  title?: string
  tag?: string
  tagTheme?: TagTheme
  translationPayload?: Record<string, string | number | undefined>
  isDanger?: boolean
  isPinned?: boolean
  info?: NotificationInfo
  statusInfo?: NotificationStatusInfo
  secondaryAction?: string
} {
  if (!notification.context) return {}

  const statusInfo = determineSaleNotificationStatusInfo(notification)
  const { deadlineDate, deadlineDays, status } = statusInfo
  const deadline = deadlineDate ? formatLocalDate(deadlineDate) : undefined
  const absDeadlineDays =
    typeof deadlineDays === 'number' ? Math.abs(deadlineDays) : undefined

  switch (status) {
    case NOTIFICATION_STATUS.AUTH_REQUIRED:
      return {
        title: 'your_item_sold_upload_auth',
        tag: determineDocsTag(deadlineDays),
        tagTheme: 'red',
        translationPayload: { deadlineDays: absDeadlineDays },
        info: {
          title: 'auth_required',
          description: 'to_complete_sale',
          translationPayload: {
            deadline,
          },
          descriptionMobile: 'to_complete_sale',
          buttonAction: `${getArtworkUrlFromRoot(
            'ask-or-sell/submit-documents',
            notification?.artwork?.artists || [],
            notification?.artwork?.name || '',
            notification?.artwork?.id || 0
          )}/Main?askId=${notification?.context?.askId}`,
          buttonText: 'upload_docs',
        },
        statusInfo,
        secondaryAction: 'upload_docs',
        isPinned: true,
      }

    case NOTIFICATION_STATUS.AUTH_OVERDUE:
      return {
        title: 'auth_overdue',
        tag: 'x_days_overdue',
        tagTheme: 'red',
        isDanger: true,
        translationPayload: {
          overdueDays: absDeadlineDays,
        },
        info: {
          title: 'auth_overdue',
          description: 'auth_for_this_sale_is_overdue',
          titleMobile: 'last_chance',
          descriptionMobile: 'please_submit_required_documents_immediately',
        },
        statusInfo,
        secondaryAction: 'upload_docs',
        isPinned: true,
      }

    case NOTIFICATION_STATUS.AUTH_REVIEW:
      return {
        title: 'sale_auth_review',
        tag: 'authentication',
        tagTheme: 'yellow',
        info: {
          title: 'auth_review',
          description: 'our_team_is_reviewing_auth_docs',
          descriptionMobile: 'our_team_is_reviewing_auth_docs',
        },
        statusInfo,
        isPinned: true,
      }

    case NOTIFICATION_STATUS.AUTH_APPROVED:
      return {
        title: 'auth_approved_ship_now',
        tag: determineShipTag(deadlineDays),
        tagTheme: 'red',
        translationPayload: { deadlineDays: absDeadlineDays },
        info: {
          title: 'auth_approved_ship_now_to_get_paid',
          description: 'please_check_email_for_shipping_instructions',
          titleMobile: 'ship_by_deadline',
          descriptionMobile: 'please_check_your_email_for_packing_guidance',
          translationPayload: {
            deadline,
          },
        },
        statusInfo,
        secondaryAction: 'print_label',
        isPinned: true,
      }

    case NOTIFICATION_STATUS.SHOULD_SHIP:
      return {
        title: 'your_item_sold_ship_now',
        tag: determineShipTag(deadlineDays),
        tagTheme: 'red',
        translationPayload: { deadlineDays: absDeadlineDays },
        info: {
          title: 'your_item_sold_ship_now_to_get_paid',
          description: 'please_check_email_for_shipping_instructions',
          titleMobile: 'ship_by_deadline',
          descriptionMobile: 'please_check_your_email_for_packing_guidance',
        },
        statusInfo,
        secondaryAction: 'print_label',
        isPinned: true,
      }

    case NOTIFICATION_STATUS.SHIPPING_OVERDUE:
      return {
        title: 'shipping_overdue',
        tag: 'x_days_overdue',
        tagTheme: 'red',
        isDanger: true,
        translationPayload: {
          overdueDays: absDeadlineDays,
        },
        info: {
          title: 'shipping_overdue',
          description: 'shipping_for_this_sale_is_overdue',
          titleMobile: 'last_chance',
          descriptionMobile: 'please_ship_the_item_sold_immediately',
        },
        statusInfo,
        secondaryAction: 'print_label',
        isPinned: true,
      }

    case NOTIFICATION_STATUS.AWAITING_LABEL:
      return {
        title: 'your_item_sold_simple',
        tag: 'awaiting_label',
        tagTheme: 'yellow',
        info: {
          title: 'your_item_sold_simple',
          description: 'shipping_label_fail_to_generate',
          titleMobile: 'awaiting_shipping_label',
          descriptionMobile: 'awaiting_shipping_label_desc_mobile',
        },
        statusInfo,
        isPinned: true,
      }

    default:
      // the user should neither send the artwork, nor upload any documents
      if (
        isPaymentFailedNotification(notification.type, notification.context)
      ) {
        return {
          title: 'sale_awaiting_buyer_payment',
          tag: 'awaiting_buyer_payment',
          tagTheme: 'yellow',
          info: {
            title: 'awaiting_buyer_payment',
            description: 'we_were_unable_to_collect_payment_automatically',
            descriptionMobile:
              'we_were_unable_to_collect_payment_automatically',
          },
        }
      }

      return { title: 'your_item_sold' }
  }
}

/**
 * Determines content which should be displayed for an order notification.
 */
export function determineOrderNotificationContent(notification: Notification): {
  info?: NotificationInfo
  isDanger?: boolean
  tag?: string
  tagTheme?: TagTheme
} {
  if (
    notification.type !== NotificationType.ORDER_CONFIRMATION ||
    !notification.context
  ) {
    return {}
  }

  if (isPaymentFailedNotification(notification.type, notification.context)) {
    return {
      tag: 'payment_failed',
      tagTheme: 'red',
      info: {
        title: 'payment_failed',
        description: 'we_were_unable_to_charge_your_card_automatically',
        descriptionMobile: 'we_were_unable_to_charge_your_card_automatically',
        buttonText: 'update_payment_method',
        buttonAction: `${routes.myAccount.settings}#billing_info`,
      },
      isDanger: true,
    }
  }

  return {}
}

export const getNotificationContent = (
  userCurrency: Currency,
  userRegion: Region | null,
  notification: Notification,
  t: Translate,
  formatCurrency: ReturnType<typeof useCurrencies>['formatCurrency']
) => {
  const {
    type,
    groupingCount,
    artwork,
    artist,
    context,
    hasAsk,
    hasBid,
    parentId,
  } = notification

  const content = TypeToContentMap[type] ?? {}
  let { title, description, action, secondaryAction } = content
  const { titleSecondary, actionTheme, secondaryActionTheme } = content
  let tag = null
  let tagTheme = null
  let isDanger = false
  let isPinned = false
  let info: NotificationInfo | null | undefined = null
  let statusInfo: NotificationStatusInfo | null | undefined = null

  const isNotificationGrouped = Boolean(groupingCount && groupingCount > 1)

  const artworkName = artwork?.name || ''
  const firstArtistName =
    artwork?.artists?.map(artist => artist.name).join(' x ') || ''
  const artistName = artist?.name

  const {
    price,
    highestBidPrice,
    lowestAskPrice,
    lowestAskPriceGbp,
    ownAskPrice,
    ownBidPrice,
  } = determineNotificationPrices(userCurrency, userRegion, notification)

  const formatPrice = (value: number) =>
    formatCurrency(value, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      from: Currencies.USER,
      to: Currencies.USER,
    })

  let translationPayload = {
    price: formatPrice(price || 0),
    highestBid: highestBidPrice ? formatPrice(highestBidPrice / 100) : '',
    lowestAsk: lowestAskPrice ? formatPrice(lowestAskPrice / 100) : '',
    ownBid: ownBidPrice ? formatPrice(ownBidPrice / 100) : '',
    ownAsk: ownAskPrice ? formatPrice(ownAskPrice / 100) : '',
    nrAsks: context?.nrAsks || '',
    nrBids: context?.nrBids || '',
  }

  switch (type) {
    case NotificationType.NEW_LOWEST_ASK_SELLER_FOLLOW:
    case NotificationType.NEW_LOWEST_ASK_SAVED_ARTWORK:
    case NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST:
      // don't show the "buy now" button if the current lowest ask does not have the same value as in the context
      if ((lowestAskPriceGbp || 0) / 100 !== context?.priceGbp) {
        action = undefined
      }
      if (isNotificationGrouped) {
        title = 'x_new_lowest_listings'
      }
      break

    // don't show the "buy now" button if the artwork does not have a lowest ask because the user cannot sell the item
    case NotificationType.NEW_HIGHEST_BID_BUYER_FOLLOW: // kept for backwards compatibility
    case NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST:
    case NotificationType.NEW_HIGHEST_BID_SAVED_ARTWORK:
      if (!lowestAskPrice) {
        action = undefined
      }
      break

    case NotificationType.NEW_HIGHEST_BID_BUYER:
    case NotificationType.NEW_LOWEST_ASK_BUYER:
    case NotificationType.ORDER_BY_ANOTHER_BUYER:
      if (!hasBid?.price) {
        action = undefined
      }

      if (!lowestAskPrice) {
        secondaryAction = undefined
      }
      break

    // don't show the "sell now" button if the artwork does not have a highest bid because the user cannot buy the item
    case NotificationType.NEW_HIGHEST_BID_SELLER:
    case NotificationType.NEW_LOWEST_ASK_SELLER:
    case NotificationType.SALE_BY_ANOTHER_SELLER:
      if (!hasAsk?.price) {
        action = undefined
      }

      if (!highestBidPrice) {
        secondaryAction = undefined
      }
      break

    case NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST_COMBINED:
      if (parentId) {
        title = 'new_highest_offer'
      } else {
        description = artistName
      }
      break

    case NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST_COMBINED:
      if (parentId) {
        title = 'new_lowest_listing'
      } else {
        description = artistName
      }
      break

    case NotificationType.SALE:
      const saleNotificationContent =
        determineSaleNotificationContent(notification)

      if (saleNotificationContent.title) {
        title = saleNotificationContent.title
      }

      if (saleNotificationContent.translationPayload) {
        translationPayload = {
          ...translationPayload,
          ...saleNotificationContent.translationPayload,
        }
      }

      tag = saleNotificationContent.tag
      tagTheme = saleNotificationContent.tagTheme
      isDanger = Boolean(saleNotificationContent.isDanger)
      isPinned = Boolean(saleNotificationContent.isPinned)
      info = saleNotificationContent.info
      statusInfo = saleNotificationContent.statusInfo
      secondaryAction = saleNotificationContent.secondaryAction

      break

    case NotificationType.ORDER_CONFIRMATION:
      const orderNotificationContent =
        determineOrderNotificationContent(notification)

      tag = orderNotificationContent.tag
      tagTheme = orderNotificationContent.tagTheme
      isDanger = Boolean(orderNotificationContent.isDanger)
      info = orderNotificationContent.info
      break
  }

  return {
    title: t(title, translationPayload),
    titleSecondary: titleSecondary
      ? t(titleSecondary, translationPayload)
      : undefined,
    action: action ? t(action, translationPayload) : undefined,
    secondaryAction: secondaryAction
      ? t(secondaryAction, translationPayload)
      : undefined,
    description: description
      ? t(description, {
          artworkName,
          artistName: firstArtistName,
          price: formatPrice(price || 0),
        })
      : undefined,
    actionTheme,
    secondaryActionTheme,
    tag: tag ? t(tag, translationPayload) : undefined,
    tagTheme,
    isDanger,
    isPinned,
    info,
    statusInfo,
    artists: context?.artists || '',
  }
}

export const getActionsBehavior = (
  props: Notification
): { primary?: string; secondary?: string } => {
  const { context, type, artwork, groupingCount } = props

  const defaultReturn = { primary: '#' }

  if (!context || !type || !artwork) return defaultReturn

  const { artists = [], name: artworkName = '', id: artworkId } = artwork
  const { askId, bidId, orderId, dhlInsurance } = context

  switch (type) {
    // view order details
    case NotificationType.ORDER_CONFIRMATION: {
      if (!askId || !bidId || !orderId) return defaultReturn
      return {
        primary: getOrderUrl(artworkId, askId, bidId, orderId),
      }
    }

    // view sale details
    case NotificationType.SALE_CONFIRMATION:
    case NotificationType.SALE: {
      if (!askId || !bidId || !orderId) return defaultReturn

      const { status } = determineSaleNotificationStatusInfo(props)

      let secondary = undefined

      switch (status) {
        case NOTIFICATION_STATUS.AUTH_APPROVED:
        case NOTIFICATION_STATUS.SHOULD_SHIP:
        case NOTIFICATION_STATUS.SHIPPING_OVERDUE:
          secondary = getLink(context.shipmentLabelUrl || context.shipmentUrl)
          break
        case NOTIFICATION_STATUS.AUTH_OVERDUE:
        case NOTIFICATION_STATUS.AUTH_REQUIRED:
          secondary = getSubmitDocumentsUrl(
            artists,
            artworkName,
            artworkId,
            askId
          )
          break
      }

      return {
        primary: getSaleUrl(artworkId, askId, bidId, orderId),
        secondary,
      }
    }

    // update listing
    case NotificationType.SALE_BY_ANOTHER_SELLER:
    case NotificationType.NEW_HIGHEST_BID_SELLER:
    case NotificationType.NEW_LOWEST_ASK_SELLER: {
      return {
        primary: getEditAskUrl(artists, artworkName, artworkId),
        secondary: getSellNowUrl(artists, artworkName, artworkId, dhlInsurance),
      }
    }

    // update offer
    case NotificationType.ORDER_BY_ANOTHER_BUYER:
    case NotificationType.NEW_HIGHEST_BID_BUYER:
    case NotificationType.NEW_LOWEST_ASK_BUYER: {
      return {
        primary: getEditBidUrl(artists, artworkName, artworkId),
        secondary: getBuyNowUrl(artists, artworkName, artworkId),
      }
    }

    // view bid/view bid
    case NotificationType.NEW_HIGHEST_BID_BUYER_FOLLOW:
    case NotificationType.NEW_LOWEST_ASK_SELLER_FOLLOW: {
      const artworkUrl = getArtworkUrl(artists, artworkName, artworkId)

      if (groupingCount && groupingCount > 0) {
        const artist = artists[0]
        if (!artist) return { primary: artworkUrl }
        return {
          primary: getArtistUrl(artist.name, artist.id) + '?sort=Lowest+Ask',
        }
      }

      return { primary: artworkUrl }
    }

    // extend offer
    case NotificationType.BID_EXPIRED_1day:
    case NotificationType.BID_EXPIRED_7days: {
      return { primary: routes.myAccount.buying }
    }

    // extend listing
    case NotificationType.ASK_EXPIRED_1day:
    case NotificationType.ASK_EXPIRED_7days: {
      return { primary: routes.myAccount.selling }
    }

    // renew offer
    case NotificationType.BID_EXPIRED: {
      return { primary: `${routes.myAccount.buying}?tab=expired_offers` }
    }

    // renew listing
    case NotificationType.ASK_EXPIRED: {
      return { primary: `${routes.myAccount.selling}?tab=expired_listings` }
    }

    case NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST:
    case NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST:
    case NotificationType.NEW_HIGHEST_BID_SAVED_ARTWORK:
    case NotificationType.NEW_LOWEST_ASK_SAVED_ARTWORK:
      return { primary: getBuyNowUrl(artists, artworkName, artworkId) }

    default:
      return { primary: '#' }
  }
}

export function isFollowedArtistNotification(type: NotificationType) {
  return (
    type === NotificationType.NEW_SALE_FOLLOW_ARTIST ||
    type === NotificationType.NEW_HIGHEST_BID_BUYER_FOLLOW ||
    type === NotificationType.NEW_LOWEST_ASK_SELLER_FOLLOW ||
    type === NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST ||
    type === NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST ||
    type === NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST_COMBINED ||
    type === NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST_COMBINED
  )
}

export function isSavedArtworkNotification(type: NotificationType) {
  return (
    type === NotificationType.NEW_SALE_SAVE_ARTWORK ||
    type === NotificationType.NEW_LOWEST_ASK_SAVED_ARTWORK ||
    type === NotificationType.NEW_HIGHEST_BID_SAVED_ARTWORK
  )
}

export function shouldDisplayArtistUrl(type: NotificationType) {
  return (
    type === NotificationType.NEW_SALE_FOLLOW_ARTIST ||
    type === NotificationType.NEW_SALE_SAVE_ARTWORK ||
    type === NotificationType.BID_EXPIRED ||
    type === NotificationType.ASK_EXPIRED ||
    type === NotificationType.NEW_LOWEST_ASK_SELLER_FOLLOW ||
    type === NotificationType.SALE_BY_ANOTHER_SELLER ||
    type === NotificationType.ORDER_BY_ANOTHER_BUYER
  )
}

export function isArtistNotification(type: NotificationType) {
  return (
    type === NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST_COMBINED ||
    type === NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST_COMBINED
  )
}

export function isCombinedNotification(type: NotificationType) {
  return (
    type === NotificationType.NEW_LOWEST_ASK_FOLLOW_ARTIST_COMBINED ||
    type === NotificationType.NEW_HIGHEST_BID_FOLLOW_ARTIST_COMBINED
  )
}

export function isSaleNotification(type: NotificationType) {
  return type === NotificationType.SALE
}

export function shouldNotificationBePinned(notification: Notification) {
  return notification.sortIndex === 1
}

export function isPaymentFailedNotification(
  type: NotificationType,
  context: INotificationContext | undefined
) {
  if (
    type !== NotificationType.ORDER_CONFIRMATION &&
    type !== NotificationType.SALE
  ) {
    return false
  }

  return context?.paymentStatus?.toLowerCase() === 'failed'
}
